import { BaseUserResponse } from "../../data";
import { AuthSession } from "../types/auth.types";
import {
  isMerchantOnboardingStage,
  MERCHANT_ONBOARDING_STAGES,
  type MerchantOnboardingStage,
} from "../types/merchant-onboarding.types";

const USER_KEY = "auth_user";
const TOKEN_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "auth_refresh_token";
const MERCHANT_ONBOARDING_STAGE_KEY = "merchant_onboarding_stage";
const LEGACY_MERCHANT_ONBOARDING_RESUME_PATH_KEY = "merchant_onboarding_resume_path";

const getStoredMerchantOnboardingStage = (
  user: BaseUserResponse,
): MerchantOnboardingStage | undefined => {
  const storedStage = localStorage.getItem(MERCHANT_ONBOARDING_STAGE_KEY);
  if (isMerchantOnboardingStage(storedStage)) return storedStage;

  const legacyResumePath = localStorage.getItem(LEGACY_MERCHANT_ONBOARDING_RESUME_PATH_KEY);
  if (legacyResumePath === "/merchant/register/verify-email") {
    return MERCHANT_ONBOARDING_STAGES.verifyEmail;
  }
  if (legacyResumePath === "/merchant/register/business-details") {
    return MERCHANT_ONBOARDING_STAGES.businessDetails;
  }

  return undefined;
};

export const authStorage = {
  load(): AuthSession | null {
    try {
      const storedUser = localStorage.getItem(USER_KEY);
      const storedToken = localStorage.getItem(TOKEN_KEY);
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (!storedUser || !storedToken || !storedRefreshToken) {
        return null;
      }

      const user = JSON.parse(storedUser) as BaseUserResponse;
      return {
        user,
        token: storedToken,
        refreshToken: storedRefreshToken,
        merchantOnboardingStage: getStoredMerchantOnboardingStage(user),
      };
    } catch (error) {
      console.error("Error loading auth state:", error);
      return null;
    }
  },

  save(session: AuthSession) {
    localStorage.setItem(USER_KEY, JSON.stringify(session.user));
    localStorage.setItem(TOKEN_KEY, session.token);
    localStorage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
    if (session.merchantOnboardingStage) {
      localStorage.setItem(MERCHANT_ONBOARDING_STAGE_KEY, session.merchantOnboardingStage);
    } else {
      localStorage.removeItem(MERCHANT_ONBOARDING_STAGE_KEY);
    }
    localStorage.removeItem(LEGACY_MERCHANT_ONBOARDING_RESUME_PATH_KEY);
  },

  updateTokens(token: string, refreshToken: string) {
    const session = authStorage.load();
    if (!session) return;
    authStorage.save({ ...session, token, refreshToken });
  },

  updateUser(user: BaseUserResponse) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  clear() {
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(MERCHANT_ONBOARDING_STAGE_KEY);
    localStorage.removeItem(LEGACY_MERCHANT_ONBOARDING_RESUME_PATH_KEY);
  },
};
